import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Rating,
  Stack,
  Typography,
  Link,
} from "@mui/material";
import { Talent, UserWorkStatus } from "../../types/users";
import { nl2br } from "../../utils/helper";
import { BaseModal, CardBase } from "../../components/global";
import dayjs from "dayjs";
import { Project } from "../../types/project";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalTalentDetailProps {
  open: boolean;
  onClose: () => void;
  talent: Talent | null;
  project?: Project | null;
}

const ModalTalentDetail = ({
  open,
  onClose,
  talent,
  project,
}: ModalTalentDetailProps) => {
  // UI Labels
  const talentDetailsLabel = useUILabel("talent.talentDetails");
  const closeLabel = useUILabel("common.close");
  const matchScoreLabel = useUILabel("project.matchScore");
  const totalScoreLabel = useUILabel("project.totalScore");
  const skillMatchLabel = useUILabel("project.skillMatch");
  const distanceLabel = useUILabel("project.distance");
  const availableLabel = useUILabel("project.available");
  const currentlyHiredLabel = useUILabel("project.currentlyHired");
  const aboutLabel = useUILabel("talent.about");
  const industriesLabel = useUILabel("talent.industries");
  const noIndustryAddedLabel = useUILabel("talent.noIndustryAdded");
  const skillsLabel = useUILabel("talent.skills");
  const noSkillAddedLabel = useUILabel("talent.noSkillAdded");
  const workStatusLabel = useUILabel("talent.workStatus");
  const availabilityLabel = useUILabel("talent.availability");
  const notSetLabel = useUILabel("talent.notSet");
  const soleProprietorLabel = useUILabel("talent.soleProprietor");
  const entrepreneurLabel = useUILabel("talent.entrepreneur");
  const partTimeLabel = useUILabel("talent.partTime");
  const workExperienceLabel = useUILabel("talent.workExperience");
  const educationLabel = useUILabel("talent.education");
  const licenseCertificatesLabel = useUILabel("talent.licenseCertificates");
  const languagesLabel = useUILabel("talent.languages");
  const moreInfoLabel = useUILabel("talent.moreInfo");
  const rateLabel = useUILabel("talent.rate");
  const commuteRadiusLabel = useUILabel("talent.commuteRadius");
  const allowBackgroundCheckLabel = useUILabel("talent.allowBackgroundCheck");
  const allowDrugTestLabel = useUILabel("talent.allowDrugTest");
  const yesLabel = useUILabel("project.yes");
  const noLabel = useUILabel("project.no");
  const nALabel = useUILabel("project.nA");
  const noAvailabilitySetLabel = useUILabel("project.noAvailabilitySet");
  const matchedWithProjectLabel = useUILabel("project.matchedWithProject");
  const otherSkillsLabel = useUILabel("project.otherSkills");
  const notMatchedLabel = useUILabel("project.notMatched");

  const footer = (
    <Button onClick={onClose} variant="contained" color="primary">
      {closeLabel}
    </Button>
  );

  // Don't render if talent is null or undefined
  if (!talent) {
    return null;
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={talent?.full_name || talentDetailsLabel}
      footer={footer}
      maxWidth="md"
      fullWidth={true}
      useGlassCard={true}
    >
      <Box sx={{
        p: 3,
        "@media (orientation: landscape) and (max-width: 900px)": {
          p: { xs: 1.5, sm: 2 },
        },
      }}>
        {/* Profile Header */}
        <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              alt={talent?.first_name || "Talent"}
              src={talent?.photo?.[0]?.original_url}
              sx={{ width: 100, height: 100, border: "2px solid rgba(255, 255, 255, 0.3)" }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: "#12903C", mb: 0.5 }}>
                {talent?.full_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {talent?.city}, {talent?.state} {talent?.zip}
              </Typography>
              <Rating
                value={talent?.total_rating || 0}
                readOnly
                precision={0.5}
                size="small"
              />
            </Box>
          </Stack>
        </CardBase>

        {/* Matching Score (for AI search results) */}
        {(talent as any)?.matching_score && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {matchScoreLabel}
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {totalScoreLabel}
                </Typography>
                <Chip
                  label={`${((talent as any).matching_score.total_score * 100).toFixed(1)}%`}
                  color={(talent as any).matching_score.total_score >= 0.7 ? "success" : (talent as any).matching_score.total_score >= 0.5 ? "warning" : "default"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              {(talent as any).matching_score.skill_match_score != null && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {skillMatchLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {((talent as any).matching_score.skill_match_score * 100).toFixed(1)}%
                  </Typography>
                </Stack>
              )}
              {(talent as any).matching_score.distance_km != null && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {distanceLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {((talent as any).matching_score.distance_km * 0.621371).toFixed(1)} miles
                  </Typography>
                </Stack>
              )}
              {(talent as any).matching_score.is_available !== undefined && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {availableLabel}
                  </Typography>
                  <Chip
                    label={(talent as any).matching_score.is_available ? yesLabel : noLabel}
                    color={(talent as any).matching_score.is_available ? "success" : "default"}
                    size="small"
                  />
                </Stack>
              )}
            </Stack>
          </CardBase>
        )}

        {/* Bio or Summary (from parsed_data for AI search) */}
        {(talent?.bio || (talent as any)?.parsed_data?.summary) && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {aboutLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
              {nl2br(talent.bio || (talent as any)?.parsed_data?.summary || "")}
            </Typography>
          </CardBase>
        )}

        {/* Industries */}
        <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
            {industriesLabel}
          </Typography>
          {(() => {
            // Support both direct business_verticals and user.business_verticals (for AI search results)
            const talentAny = talent as any;
            const businessVerticals = talent?.business_verticals || talentAny?.user?.business_verticals || [];
            if (businessVerticals.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {noIndustryAddedLabel}
                </Typography>
              );
            }
            return (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {businessVerticals.map((it: any) => (
                  <Chip
                    label={it.name || it}
                    key={it.id || it}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(18, 144, 60, 0.1)",
                      color: "#12903C",
                      border: "1px solid rgba(18, 144, 60, 0.2)",
                    }}
                  />
                ))}
              </Stack>
            );
          })()}
        </CardBase>

        {/* Skills */}
        <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
            {skillsLabel}
          </Typography>
          {(() => {
            // Support both direct skills, user.skills, and parsed_data.skills (for AI search results)
            const talentAny = talent as any;
            const parsedSkills = talentAny?.parsed_data?.skills || [];
            const dbSkills = talent?.skills || talentAny?.user?.skills || [];

            // If we have parsed_data skills, show them with project matching
            if (parsedSkills.length > 0 && project?.skills && project.skills.length > 0) {
              // Normalize skill name for matching
              const normalizeSkillName = (name: string): string => {
                return name.toLowerCase().replace(/[\.\-\s]/g, "").trim();
              };
              const getBaseName = (name: string): string => {
                return name.toLowerCase().trim().replace(/\.(js|ts|jsx|tsx|vue|py|java|php|rb|go|rs)$/, "");
              };

              const matchedSkills: string[] = [];
              const unmatchedSkills: string[] = [];

              parsedSkills.forEach((skill: string) => {
                const isMatched = (project?.skills || []).some((projectSkill: any) => {
                  if (!projectSkill?.name) return false;
                  const skillNorm = normalizeSkillName(skill);
                  const projectNorm = normalizeSkillName(projectSkill.name);
                  const skillBase = getBaseName(skill);
                  const projectBase = getBaseName(projectSkill.name);

                  return skillNorm === projectNorm ||
                    skillBase === projectBase ||
                    (skillNorm.length >= 3 && projectNorm.length >= 3 &&
                      (skillNorm.includes(projectNorm) || projectNorm.includes(skillNorm)));
                });

                if (isMatched) {
                  matchedSkills.push(skill);
                } else {
                  unmatchedSkills.push(skill);
                }
              });

              return (
                <Stack spacing={2}>
                  {matchedSkills.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#12903C" }}>
                        {matchedWithProjectLabel} ({matchedSkills.length})
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {matchedSkills.map((skill: string, idx: number) => (
                          <Chip
                            key={idx}
                            label={skill}
                            size="small"
                            sx={{
                              backgroundColor: "#12903C",
                              color: "#ffffff",
                              fontWeight: 600,
                              border: "1px solid rgba(18, 144, 60, 0.3)",
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {unmatchedSkills.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
                        {otherSkillsLabel} ({unmatchedSkills.length})
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {unmatchedSkills.map((skill: string, idx: number) => (
                          <Chip
                            key={idx}
                            label={skill}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(18, 144, 60, 0.1)",
                              color: "#12903C",
                              border: "1px solid rgba(18, 144, 60, 0.2)",
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              );
            }

            // Fallback to database skills or parsed skills without matching
            const skills = parsedSkills.length > 0 ? parsedSkills : dbSkills;
            if (skills.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {noSkillAddedLabel}
                </Typography>
              );
            }
            return (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {skills.map((it: any, idx: number) => (
                  <Chip
                    label={it.name || it}
                    key={it.id || it || idx}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(18, 144, 60, 0.1)",
                      color: "#12903C",
                      border: "1px solid rgba(18, 144, 60, 0.2)",
                    }}
                  />
                ))}
              </Stack>
            );
          })()}
        </CardBase>

        {/* Work Status & Availability */}
        <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#12903C", fontWeight: 600 }}>
                {workStatusLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {talent?.talent?.work_status == UserWorkStatus.SOLE_PROPRIETOR
                  ? soleProprietorLabel
                  : talent?.talent?.work_status == UserWorkStatus.ENTREPRENEUR
                    ? entrepreneurLabel
                    : talent?.talent?.work_status == UserWorkStatus.PART_TIME
                      ? partTimeLabel
                      : notSetLabel}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#12903C", fontWeight: 600 }}>
                {availabilityLabel}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {talent?.talent?.avail_monday && (
                  <Chip label="Mon" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_tuesday && (
                  <Chip label="Tue" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_wednesday && (
                  <Chip label="Wed" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_thursday && (
                  <Chip label="Thu" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_friday && (
                  <Chip label="Fri" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_saturday && (
                  <Chip label="Sat" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {talent?.talent?.avail_sunday && (
                  <Chip label="Sun" size="small" sx={{ backgroundColor: "rgba(18, 144, 60, 0.1)", color: "#12903C" }} />
                )}
                {!talent?.talent?.avail_monday &&
                  !talent?.talent?.avail_tuesday &&
                  !talent?.talent?.avail_wednesday &&
                  !talent?.talent?.avail_thursday &&
                  !talent?.talent?.avail_friday &&
                  !talent?.talent?.avail_saturday &&
                  !talent?.talent?.avail_sunday && (
                    <Typography variant="body2" color="text.secondary">
                      {noAvailabilitySetLabel}
                    </Typography>
                  )}
              </Stack>
            </Box>
          </Stack>
        </CardBase>

        {/* Work Experience (from parsed_data for AI search or work_experiences from DB) */}
        {(((talent as any)?.parsed_data?.experience && (talent as any).parsed_data.experience.length > 0) || (talent?.work_experiences && talent.work_experiences.length > 0)) && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {workExperienceLabel}
            </Typography>
            <Stack spacing={2}>
              {/* Show parsed_data experience first (AI search), then fallback to DB work_experiences */}
              {((talent as any)?.parsed_data?.experience && (talent as any).parsed_data.experience.length > 0) ? (
                (talent as any).parsed_data.experience.map((exp: any, idx: number) => (
                  <Box key={idx}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#12903C", mb: 0.5 }}>
                      {exp.position || "N/A"} {exp.position && exp.company && "•"} {exp.company || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
                      {exp.start_date && `${exp.start_date}`}
                      {exp.start_date && exp.end_date && " - "}
                      {exp.end_date || (exp.start_date && "Present") || "N/A"}
                    </Typography>
                    {exp.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
                        {exp.description}
                      </Typography>
                    )}
                    {idx < (talent as any).parsed_data.experience.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))
              ) : (
                talent.work_experiences.map((exp: any, idx: number) => (
                  <Box key={idx}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#12903C", mb: 0.5 }}>
                      {exp.company || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
                      {exp.start_period_month && exp.start_period_year
                        ? `${dayjs()
                          .set("month", exp.start_period_month - 1)
                          .set("year", exp.start_period_year)
                          .format("MMM YYYY")} to ${exp.end_period_month && exp.end_period_year
                            ? dayjs()
                              .set("month", exp.end_period_month - 1)
                              .set("year", exp.end_period_year)
                              .format("MMM YYYY")
                            : "Present"
                        }`
                        : "N/A"}
                      {exp.city && ` • ${exp.city}`}
                    </Typography>
                    {exp.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                        {exp.description}
                      </Typography>
                    )}
                    {idx < talent.work_experiences.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))
              )}
            </Stack>
          </CardBase>
        )}

        {/* Education (from parsed_data for AI search or educations from DB) */}
        {(((talent as any)?.parsed_data?.education && (talent as any).parsed_data.education.length > 0) || (talent?.educations && talent.educations.length > 0)) && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {educationLabel}
            </Typography>
            <Stack spacing={2}>
              {/* Show parsed_data education first (AI search), then fallback to DB educations */}
              {((talent as any)?.parsed_data?.education && (talent as any).parsed_data.education.length > 0) ? (
                (talent as any).parsed_data.education.map((edu: any, idx: number) => (
                  <Box key={idx}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#12903C", mb: 0.5 }}>
                      {edu.field || edu.institution || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
                      {edu.degree && `${edu.degree}, `}
                      {edu.institution}
                      {edu.graduation_year && ` • ${edu.graduation_year}`}
                    </Typography>
                    {idx < (talent as any).parsed_data.education.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))
              ) : (
                talent.educations.map((edu: any, idx: number) => (
                  <Box key={idx}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#12903C", mb: 0.5 }}>
                      {edu.school || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
                      {edu.start_date
                        ? `${dayjs(edu.start_date).format("MM/DD/YYYY")} to ${edu.end_date
                          ? dayjs(edu.end_date).format("MM/DD/YYYY")
                          : "Present"
                        }`
                        : "N/A"}
                    </Typography>
                    {edu.degree && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Degree:</strong> {edu.degree}
                      </Typography>
                    )}
                    {edu.field_of_study && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Field of Study:</strong> {edu.field_of_study}
                      </Typography>
                    )}
                    {idx < talent.educations.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))
              )}
            </Stack>
          </CardBase>
        )}

        {/* Certifications */}
        {talent?.certifications && talent.certifications.length > 0 && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {licenseCertificatesLabel}
            </Typography>
            <Stack spacing={1.5}>
              {talent.certifications.map((cert: any, idx: number) => (
                <Box key={idx}>
                  {cert.certification?.name ? (
                    cert.certification_url ? (
                      <Link
                        href={cert.certification_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: "#12903C", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        {cert.certification.name}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#12903C" }}>
                        {cert.certification.name}
                      </Typography>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {nALabel}
                    </Typography>
                  )}
                  {cert.expiration_date && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                      Expires: {dayjs(cert.expiration_date).format("MMM DD, YYYY")}
                    </Typography>
                  )}
                  {idx < talent.certifications.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </Box>
              ))}
            </Stack>
          </CardBase>
        )}

        {/* Languages */}
        {talent?.languages && talent.languages.length > 0 && (
          <CardBase nested={true} useGlassCard={true} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
              {languagesLabel}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {talent.languages.map((lang: any) => (
                <Chip
                  label={lang.name || lang}
                  key={lang.id || lang}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                  }}
                />
              ))}
            </Stack>
          </CardBase>
        )}

        {/* More Info */}
        <CardBase nested={true} useGlassCard={true}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
            {moreInfoLabel}
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" color="text.secondary" component="span">
                <strong>{rateLabel}:</strong> ${talent?.min_pay || nALabel} - ${talent?.max_pay || nALabel} /hr
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" component="span">
                <strong>{commuteRadiusLabel}:</strong> {talent?.commute_radius || nALabel} miles
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" component="span">
                <strong>{allowBackgroundCheckLabel}:</strong> {talent?.allow_background_check === true ? yesLabel : talent?.allow_background_check === false ? noLabel : nALabel}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" component="span">
                <strong>{allowDrugTestLabel}:</strong> {talent?.allow_drug_test === true ? yesLabel : talent?.allow_drug_test === false ? noLabel : nALabel}
              </Typography>
            </Box>
            {talent?.linkedin && (
              <Box>
                <Typography variant="body2" color="text.secondary" component="span">
                  <strong>LinkedIn:</strong>{" "}
                  <Link href={talent.linkedin} target="_blank" rel="noopener noreferrer" sx={{ color: "#12903C" }}>
                    {talent.linkedin}
                  </Link>
                </Typography>
              </Box>
            )}
            {talent?.website && (
              <Box>
                <Typography variant="body2" color="text.secondary" component="span">
                  <strong>Website:</strong>{" "}
                  <Link href={talent.website} target="_blank" rel="noopener noreferrer" sx={{ color: "#12903C" }}>
                    {talent.website}
                  </Link>
                </Typography>
              </Box>
            )}
          </Stack>
        </CardBase>
      </Box>
    </BaseModal>
  );
};
export default ModalTalentDetail;
